import { toPng } from 'html-to-image'
import { getNodesBounds, getViewportForBounds, type Node } from '@xyflow/react'
import { jsPDF } from 'jspdf'

/**
 * Render the whole org-chart diagram (all nodes, regardless of current
 * zoom/pan) to a PNG data URL by fitting the node bounds into an offscreen
 * viewport transform. Uses the React Flow recommended export recipe.
 */
async function renderPng(
  nodes: Node[]
): Promise<{ dataUrl: string; width: number; height: number }> {
  const bounds = getNodesBounds(nodes)
  const padding = 40
  const width = Math.max(Math.ceil(bounds.width) + padding * 2, 320)
  const height = Math.max(Math.ceil(bounds.height) + padding * 2, 240)
  const viewport = getViewportForBounds(bounds, width, height, 0.2, 2, 0.1)
  const el = document.querySelector('.react-flow__viewport') as HTMLElement | null
  if (!el) throw new Error('Diagram not ready')
  const dataUrl = await toPng(el, {
    backgroundColor: '#ffffff',
    width,
    height,
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
    },
  })
  return { dataUrl, width, height }
}

function triggerDownload(dataUrl: string, filename: string) {
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

export async function exportPng(nodes: Node[], filename = 'org-chart.png') {
  const { dataUrl } = await renderPng(nodes)
  triggerDownload(dataUrl, filename)
}

export async function exportPdf(nodes: Node[], filename = 'org-chart.pdf') {
  const { dataUrl, width, height } = await renderPng(nodes)
  const orientation = width >= height ? 'landscape' : 'portrait'
  const pdf = new jsPDF({ orientation, unit: 'px', format: [width, height] })
  pdf.addImage(dataUrl, 'PNG', 0, 0, width, height)
  pdf.save(filename)
}
